import React, { useState, useRef, useEffect } from 'react';

// Предел ожидания одного шага отсчёта. speechSynthesis в Chrome умеет
// молча не вызвать ни onend, ни onerror — без этой страховки отсчёт
// зависал бы навсегда и тест не начинался вообще.
const STEP_HARD_TIMEOUT_MS = 4000;

/**
 * Отсчёт "3 - 2 - 1 - Начинайте" перед пробой на время.
 *
 * Здесь исправлен баг, который жил в Coding/CodingV2: раньше отсчёт
 * крутился внутри useEffect с зависимостями [phase, mode], и любая
 * смена этих значений вызывала cleanup, который ставил cancelled=true.
 * Колбэк завершения речи срабатывал позже, читал протухший флаг и
 * молча НЕ вызывал startListening() — микрофон не включался, ошибок
 * в консоли не было.
 *
 * Теперь последовательность живёт в отдельном компоненте, запускается
 * ровно один раз за монтирование (startedRef) и прерывается ТОЛЬКО
 * настоящим размонтированием (aliveRef). Перерисовки родителя на неё
 * не влияют.
 *
 * onReady вызывается строго после того, как "Начинайте" реально
 * дозвучало: именно в этот момент, и ни секундой раньше, стартуют
 * часы пробы и микрофон.
 */
export default function CountdownGate({ speak, onReady, steps = ['Три', 'Два', 'Один'], goLabel = 'Начинайте' }) {
  const [current, setCurrent] = useState(steps[0] || goLabel);

  const startedRef = useRef(false);
  const aliveRef = useRef(true);
  const speakRef = useRef(speak);
  const onReadyRef = useRef(onReady);

  useEffect(() => { speakRef.current = speak; }, [speak]);
  useEffect(() => { onReadyRef.current = onReady; }, [onReady]);

  useEffect(() => {
    aliveRef.current = true;
    return () => { aliveRef.current = false; };
  }, []);

  useEffect(() => {
    if (startedRef.current) return;
    startedRef.current = true;

    const sayOnce = (text) => new Promise((resolve) => {
      let settled = false;
      const settle = () => {
        if (settled) return;
        settled = true;
        clearTimeout(guard);
        resolve();
      };
      const guard = setTimeout(settle, STEP_HARD_TIMEOUT_MS);
      const fn = speakRef.current;
      if (!fn) { settle(); return; }
      fn(text, settle);
    });

    const run = async () => {
      for (const step of steps) {
        if (!aliveRef.current) return;
        setCurrent(step);
        await sayOnce(step);
      }
      if (!aliveRef.current) return;
      setCurrent(goLabel);
      await sayOnce(goLabel);
      if (!aliveRef.current) return;
      if (onReadyRef.current) onReadyRef.current();
    };

    run();
    // Запуск строго один раз за монтирование — зависимости здесь
    // намеренно пустые, см. комментарий выше.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  return (
    <div className="fixed inset-0 z-50 flex flex-col items-center justify-center bg-background">
      <div className="text-8xl font-bold text-accent sm:text-9xl">{current}</div>
    </div>
  );
}
